import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PiMapPinLight } from "react-icons/pi";
import useBookingApp from "../../../../../hooks/useBookingApp";
import { saveService } from "../../../../../dispatchers/templateSectionDispatchers";
import { showError } from "../../../../ToastedMessage/ToastedMessage";
import {
  createRyftSession,
  createPaymentAudit,
} from "../../../../../dispatchers/bookingDispatchers";
import CustomLoader from "../../../../controls/CustomLoader";
import { showSuccess } from "../../../../ToastedMessage/ToastedMessage";

export default function PaymentDetails({ mainColor, handleNextStep }) {
  const { bookingSectionState } = useBookingApp();
  const {
    bookingModel,
    selectedServiceBookingSlot,
    selectedBranch,
    payAmount,
  } = bookingSectionState;
  const { Email } = bookingModel;
  const formRef = useRef(null);
  const [isCardValid, setIsCardValid] = useState(false);
  const [clientSecret, setClientSecret] = useState(null); // Get this from your backend
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        // Fallback logic for customerEmail
        let customerEmail = Email;

        if (!customerEmail) {
          const storedData = localStorage.getItem("about-you-data");
          if (storedData) {
            const parsed = JSON.parse(storedData);
            customerEmail = parsed?.emailAddress || "";
          }
        }

        const body = {
          returnUrl: "https://www.invatechhealth.com",
          currency: "GBP",
          customerEmail,
          amount: Math.round(Number(payAmount) * 100),
        };

        const response = await createRyftSession({ data: body }); // returns a JSON string
        const parsedData = JSON.parse(response);

        if (parsedData?.clientSecret) {
          setClientSecret(parsedData.clientSecret);
        } else {
          console.warn("clientSecret not found in parsed response");
        }
      } catch (error) {
        console.error("Failed to fetch or parse client secret", error);
      }
    };

    fetchClientSecret();
  }, []);

  useEffect(() => {
    if (!clientSecret || !window.Ryft) return;

    window.Ryft.init({
      publicKey: process.env.REACT_APP_PUBLIC_KEY, // replace with your actual public key
      clientSecret,
      googlePay: {
        merchantIdentifier: "merchant_123",
        merchantName: "TitanVerse Limited",
        merchantCountryCode: "44",
      },
      applePay: {
        merchantName: "TitanVerse Limited",
        merchantCountryCode: "44",
      },
      style: {
        borderRadius: 10,
      },
    });

    const handleCardValidation = (e) => {
      setIsCardValid(e.isValid);
    };

    const handlePaymentResult = async (paymentSession) => {
      const session = paymentSession?.paymentSession;

      if (["Approved", "Captured"].includes(session?.status)) {
        try {
          const auditPayload = {
            _id: paymentSession.id,

            paymentSessionId: clientSecret,
            amount: session.amount,
            currency: session.currency,
            returnUrl: session.returnUrl,
            status: session.status,
            enabledPaymentMethods: session.enabledPaymentMethods,
            paymentMethod: {
              type: session.paymentMethod?.type,
              tokenizedDetails: {
                id: session?.paymentMethod?.tokenizedDetails?.id,
                stored: false,
              },
              card: {
                scheme: session.paymentMethod?.card?.scheme,
                last4: session.paymentMethod?.card?.last4,
              },
            },
            customerEmail: session.customerEmail,
            createdTimestamp: session.createdTimestamp,
            lastUpdatedTimestamp: session.lastUpdatedTimestamp,
          };

          // await createPaymentAudit({ data: auditPayload });

          const objectModel = {
            serviceModel: bookingModel,
            serviceSlot: selectedServiceBookingSlot,
            paymentAudit: auditPayload,
          };

          await saveService(objectModel);
          showSuccess("Payment Successful", true, false);
          handleNextStep();
          localStorage.clear();
        } catch (err) {
          console.error("Error handling wallet payment result", err);
          showError("Something went wrong after Google Pay.");
        }
      } else if (session?.lastError) {
        const userError = window.Ryft.getUserFacingErrorMessage(
          session.lastError
        );
        document.getElementById("ryft-pay-error").innerText = userError;
      }
    };

    window.Ryft.addEventHandler("cardValidationChanged", handleCardValidation);
    window.Ryft.addEventHandler(
      "walletPaymentSessionResult",
      handlePaymentResult
    );

    // Cleanup the event handlers manually
    return () => {
      // window.Ryft.removeEventHandler("cardValidationChanged", handleCardValidation);
      // window.Ryft.removeEventHandler("walletPaymentSessionResult", handlePaymentResult);
    };
  }, [clientSecret]);

  useEffect(() => {
    const interval = setInterval(() => {
      const divider = document.querySelector(".divider");

      if (divider) {
        divider.remove(); // Removes the divider from DOM
      }

      const stillExists = document.querySelector(".divider");
      if (!stillExists) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true); // Show loader and hide button

    try {
      const paymentSession = await window.Ryft.attemptPayment();

      if (
        paymentSession.status === "Approved" ||
        paymentSession.status === "Captured"
      ) {
        const auditPayload = {
          _id: paymentSession.id,
          paymentSessionId: clientSecret,
          amount: paymentSession.amount,
          currency: paymentSession.currency,
          returnUrl: paymentSession.returnUrl,
          status: paymentSession.status,
          enabledPaymentMethods: paymentSession.enabledPaymentMethods,
          paymentMethod: {
            type: paymentSession.paymentMethod?.type,
            tokenizedDetails: {
              id: paymentSession?.paymentMethod?.tokenizedDetails?.id,
              stored: false,
            },
            card: {
              scheme: paymentSession.paymentMethod?.card?.scheme,
              last4: paymentSession.paymentMethod?.card?.last4,
            },
          },
          customerEmail: paymentSession.customerEmail,
          createdTimestamp: paymentSession.createdTimestamp,
          lastUpdatedTimestamp: paymentSession.lastUpdatedTimestamp,
        };

        setIsCardValid(false);

        // await createPaymentAudit({ data: auditPayload });
        const objectModel = {
          serviceModel: bookingModel,
          serviceSlot: selectedServiceBookingSlot,
          paymentAudit: auditPayload,
        };

        await saveService(objectModel);
        setProcessing(false); // Done, hide loader
        showSuccess("Payment Successfull", true, 3000);
        handleNextStep();
        localStorage.clear();
      } else if (paymentSession.status === "PendingPayment") {
        setProcessing(false); // Optionally handle this case
      } else if (paymentSession.lastError) {
        const userError = window.Ryft.getUserFacingErrorMessage(
          paymentSession.lastError
        );
        document.getElementById("ryft-pay-error").innerText = userError;
        setProcessing(false); // Show pay button again
      }
    } catch (error) {
      // console.error("Payment failed or audit call failed:", error);
      document.getElementById("ryft-pay-error").innerText =
        "Payment failed. Try again.";
      setProcessing(false); // Show pay button again
    }
  };

  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
      exit={{ y: 50, opacity: 0 }}
      className="bf-content-wrapper payment-details-wrapper"
    >
      <div className="bf-content-body">
        <div className="bf-content-header">
          <h2 className="bf-title" style={{ color: mainColor }}>
            Confirm Your Appointment Details
          </h2>
        </div>

        <div className="content">
          <div className="details-box appointment-details">
            <div className="title-address">
              <div className="title-box">
                <p className="title consultation-title">
                  Travel Vaccine Consultation
                </p>
                <p className="consultation-date-time">
                  {bookingModel?.AppointmentDate && (
                    <span className="date">
                      {new Date(
                        bookingModel?.AppointmentDate
                      ).toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "2-digit",
                        month: "long",
                      })}
                    </span>
                  )}
                  <span className="time">
                    {selectedServiceBookingSlot?.startTime} -{" "}
                    {selectedServiceBookingSlot?.endTime} (1h)
                  </span>
                </p>
              </div>
              <div className="address-box">
                <PiMapPinLight size={20} />
                <p>{selectedBranch?.pharmacyAddress}</p>
              </div>
            </div>
          </div>

          <div className="details-box deposit-details">
            <p className="title deposit-title">
              Travel Vaccine Consultation - Deposit
            </p>
            <div className="title-amount">
              <p className="amount-title">Travel Vaccine Consultation</p>
              <p className="amount">£{payAmount}</p>
            </div>
            <p className="note">
              This deposit secures your slot and will be deducted from the total
              cost
            </p>
            <div className="total-box">
              <p className="total-title">Total</p>
              <p className="total-amount">£{payAmount}</p>
            </div>
          </div>

          <div className="details-box payment-details">
            <p className="title payment-title">Payment</p>
            <div className="payment-form">
              <div className="payment-methods-header">
                <p className="methods-title">Payment Methods</p>
              </div>
              <div className="Ryft--paysection">
                <form
                  id="ryft-pay-form"
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className="Ryft--payform"
                >
                  {processing ? (
                    <div className="payment-processing-loader">
                      <CustomLoader width={45} height={45} />
                    </div>
                  ) : (
                    <button
                      id="pay-btn"
                      className={!isCardValid ? "pay-btn-disabled" : "pay-btn"}
                      disabled={!isCardValid}
                      type="submit"
                    >
                      Pay Now
                    </button>
                  )}

                  <div
                    id="ryft-pay-error"
                    style={{ color: "red", marginTop: "10px" }}
                  ></div>
                  {/* <div className="payment-methods-wrapper">
                    <button className="g-pay-btn">
                      Pay with <img src={applePay} alt="apple-pay" />
                    </button>
                  </div> */}
                </form>
              </div>
            </div>
          </div>

          <div className="details-box cancellation-policy">
            <p className="title">Cancellation Policy</p>
            <p className="sub-text">
              Free cancellations up to 48 hours before your appointment. After
              that, deposits are non-refundable.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
